import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import Icon from "../icons";
import s from "./ui.module.scss";

export type ModalSize = "sm" | "md" | "lg";

const SIZE: Record<ModalSize, string> = {
  sm: s.modalSm,
  md: s.modalMd,
  lg: s.modalLg,
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  size?: ModalSize;
  /** Right-aligned footer actions (e.g. Cancel / Save buttons). */
  footer?: React.ReactNode;
  /** Close when the backdrop is clicked. Default true. */
  closeOnBackdrop?: boolean;
  children: React.ReactNode;
}

// Rendered into <body> via a portal so it overlays everything and isn't clipped
// by the shell's panels. Closes on Escape and (optionally) backdrop click; locks
// body scroll while open.
const Modal = ({
  open,
  onClose,
  title,
  size = "md",
  footer,
  closeOnBackdrop = true,
  children,
}: ModalProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Callers pass inline `onClose` closures, new on every render. Read it through
  // a ref so the open-effect runs once per open: re-running it re-focused the
  // dialog and stole focus from inputs on every keystroke (CON-95).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    dialogRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className={s.modalBackdrop}
      onMouseDown={closeOnBackdrop ? onClose : undefined}
    >
      <div
        ref={dialogRef}
        className={`${s.modal} ${SIZE[size]}`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={s.modalHeader}>
          <h2 className={s.modalTitle}>{title}</h2>
          <button
            type="button"
            className={s.modalClose}
            onClick={onClose}
            aria-label="Close"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className={s.modalBody}>{children}</div>

        {footer && <div className={s.modalFooter}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
